import { BACKEND_API_BASE_URL } from '../config';

const API_BASE = `${BACKEND_API_BASE_URL}/api/attendance`;

async function request(path = '', method = '', body, authToken) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' ,
       ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
       },
    body: body ? JSON.stringify(body) : undefined,
  });
  const ct = res.headers.get('content-type') || '';
  const data = ct.includes('application/json') ? await res.json() : await res.text();
  
  if (!res.ok) {
    console.error('API Error:', {
      status: res.status,
      statusText: res.statusText,
      url: res.url,
      data: data
    });
    // If data is already parsed JSON and has error info, throw it
    if (typeof data === 'object' && data.error) {
      throw data;
    }
    // Otherwise create a structured error
    const error = new Error(typeof data === 'string' ? data : 'Unknown error');
    error.status = res.status;
    error.statusText = res.statusText;
    throw error;
  }
  
  return data;
}


export const AttendanceAPI = {
  list:   (authToken)             => request('/', 'GET', undefined, authToken),
  get:    (id, authToken)         => request(`/${id}`, 'GET', undefined, authToken),
  create: (row, authToken)        => request('/', 'POST', row, authToken),
  bulk:   (payload, authToken)    => request('/take', 'POST', payload, authToken),
  update: (id, data, authToken)   => request(`/${id}`, 'PUT', data, authToken),
  remove: (id, authToken)         => request(`/${id}`, 'DELETE', undefined, authToken),
  getSessionsByClass: (classId, periodId, authToken) => {
    const query = periodId ? `?periodId=${periodId}` : '';
    return request(`/sessions/class/${classId}${query}`, 'GET', undefined, authToken);
  },
  getSessionsByClassFiltered: (classId, filters, authToken) => {
    const queryParams = new URLSearchParams();
    if (filters.periodId) queryParams.append('periodId', filters.periodId);
    if (filters.date) queryParams.append('date', filters.date);
    if (filters.instructorId) queryParams.append('instructorId', filters.instructorId);
    
    const query = queryParams.toString();
    return request(`/sessions/class/${classId}/filtered${query ? `?${query}` : ''}`, 'GET', undefined, authToken);
  },
  getSessionsBySchool: (schoolId, pagination, authToken) => {
    const queryParams = new URLSearchParams();
    if (pagination?.page !== undefined) queryParams.append('page', pagination.page);
    if (pagination?.pageSize !== undefined) queryParams.append('pageSize', pagination.pageSize);
    
    const query = queryParams.toString();
    return request(`/sessions/school/${schoolId}${query ? `?${query}` : ''}`, 'GET', undefined, authToken);
  },
  getSessionsBySchoolFiltered: (schoolId, filters, pagination, authToken) => {
    const queryParams = new URLSearchParams();
    if (filters.classId) queryParams.append('classId', filters.classId);
    if (filters.date) queryParams.append('date', filters.date);
    if (filters.instructorId) queryParams.append('instructorId', filters.instructorId);
    if (filters.periodId) queryParams.append('periodId', filters.periodId);
    if (pagination?.page !== undefined) queryParams.append('page', pagination.page);
    if (pagination?.pageSize !== undefined) queryParams.append('pageSize', pagination.pageSize);
    
    const query = queryParams.toString();
    return request(`/sessions/school/${schoolId}/filtered${query ? `?${query}` : ''}`, 'GET', undefined, authToken);
  },
  getSessionStudents: (sessionId, schoolId, authToken) => {
    return request(`/sessions/${sessionId}/students/${schoolId}`, 'GET', undefined, authToken);
  },
  updateSession: (sessionId, data, authToken) => {
    return request(`/sessions/${sessionId}`, 'PUT', data, authToken);
  },
  deleteSession: (classId, date, periodId, authToken) => {
    const query = periodId ? `?periodId=${periodId}` : '';
    return request(`/sessions/${classId}/${date}${query}`, 'DELETE', undefined, authToken);
  },
  // Add individual record creation/update endpoints for notes functionality
  createRecord: (attendanceId, recordData, authToken) => {
    return request(`/${attendanceId}/records`, 'POST', recordData, authToken);
  },
  updateRecord: (recordId, recordData, authToken) => {
    return request(`/records/${recordId}`, 'PUT', recordData, authToken);
  },
  // Get attendance analytics for all classes in a school
  getSchoolAttendance: (authToken, params) => {
    const { schoolId, ...queryParams } = params;
    const urlParams = new URLSearchParams();
    if (queryParams.startDate) urlParams.append('startDate', queryParams.startDate);
    if (queryParams.endDate) urlParams.append('endDate', queryParams.endDate);
    if (queryParams.periodId) urlParams.append('periodId', queryParams.periodId);
    
    const query = urlParams.toString();
    return request(`/school/${schoolId}/analytics${query ? `?${query}` : ''}`, 'GET', undefined, authToken);
  }
};
